import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import ElevationMeasurementTool from "@vision/core-map-api/src/measures/tools/internal/ElevationMeasurementTool";
import CircleMeasurementTool from "@vision/core-map-api/src/measures/tools/internal/CircleMeasurementTool";
import LineMeasurementTool from "@vision/core-map-api/src/measures/tools/internal/LineMeasurementTool";
import PolygonMeasurementTool from "@vision/core-map-api/src/measures/tools/internal/PolygonMeasurementTool";
import SquareMeasurementTool from "@vision/core-map-api/src/measures/tools/internal/SquareMeasurementTool";
import HeightMeasurementTool from "@vision/core-map-api/src/measures/tools/internal/HeightMeasurementTool";
import PointDSMMeasurementTool from "@vision/core-map-api/src/measures/tools/internal/PointDSMMeasurementTool";
import PointDTMMeasurementTool from "@vision/core-map-api/src/measures/tools/internal/PointDTMMeasurementTool";
import PointMeasurementTool from "@vision/core-map-api/src/measures/tools/internal/PointMeasurementTool";
import RelativeElevationMeasurementTool from "@vision/core-map-api/src/measures/tools/internal/RelativeElevationMeasurementTool";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { IPopup } from "../../interfaces/i-popup";
import { MeasurementTool } from "../../model/measurementtool";

@Injectable({
  providedIn: "root",
})
export class MeasurementToolService {
  private enableToolsSubject = new Subject<boolean>();

  public enableTools$ = this.enableToolsSubject.asObservable();

  protected groundElevationPopupSubject = new Subject<IPopup>();

  public groundElevationPopup$ = this.groundElevationPopupSubject.asObservable();

  protected pointInfoPopupSubject = new Subject<IPopup>();

  public pointInfoPopup$ = this.pointInfoPopupSubject.asObservable();

  createMeasurementTool(
    map: any,
    toolTypeId: number,
    mapId: number,
    measurementSystem: number,
    lengthUnits: string,
    areaUnits: string,
  ): any {
    let externalTool: any;
    switch (toolTypeId) {
      case ViewerConstants.EXTERNAL_TOOLS.MEASURE_HEIGHT: {
        externalTool = new HeightMeasurementTool({
          map: map,
          measureUnits: measurementSystem,
          lengthUnits,
        });
        break;
      }
      case ViewerConstants.EXTERNAL_TOOLS.MEASURE_LINE: {
        externalTool = new LineMeasurementTool({
          map: map,
          measureUnits: measurementSystem,
          lengthUnits,
        });
        break;
      }
      case ViewerConstants.EXTERNAL_TOOLS.MEASURE_POLYGON: {
        externalTool = new PolygonMeasurementTool({
          map: map,
          measureUnits: measurementSystem,
          areaUnits,
        });
        break;
      }
      case ViewerConstants.EXTERNAL_TOOLS.MEASURE_CIRCLE: {
        externalTool = new CircleMeasurementTool({
          map: map,
          measureUnits: measurementSystem,
          areaUnits,
        });
        break;
      }
      case ViewerConstants.EXTERNAL_TOOLS.MEASURE_ELEVATION: {
        externalTool = new ElevationMeasurementTool({
          map: map,
          lengthUnits,
          measureUnits: measurementSystem,
          showPopup: true,
          callback: this.updateGroundElevationPopupSubject.bind(this),
        });
        break;
      }
      case ViewerConstants.EXTERNAL_TOOLS.MEASURE_AREA: {
        externalTool = new SquareMeasurementTool({
          map: map,
          measureUnits: measurementSystem,
          areaUnits,
        });
        break;
      }
      case ViewerConstants.EXTERNAL_TOOLS.MEASURE_POINT_3D: {
        externalTool = new PointDTMMeasurementTool({
          map: map,
          measureUnits: measurementSystem,
          lengthUnits,
          callback: this.updatePointInfoPopupSubject.bind(this),
        });
        break;
      }
      case ViewerConstants.EXTERNAL_TOOLS.MEASURE_POINT_SURVEY: {
        externalTool = new PointDSMMeasurementTool({
          map: map,
          measureUnits: measurementSystem,
          lengthUnits,
          callback: this.updatePointInfoPopupSubject.bind(this),
        });
        break;
      }
      case ViewerConstants.EXTERNAL_TOOLS.MEASURE_POINT: {
        externalTool = new PointMeasurementTool({
          map: map,
          measureUnits: measurementSystem,
          lengthUnits,
          callback: this.updatePointInfoPopupSubject.bind(this),
        });
        break;
      }
      case ViewerConstants.EXTERNAL_TOOLS.RELATIVE_MEASURE_POINT: {
        externalTool = new RelativeElevationMeasurementTool({
          map: map,
          lengthUnits,
          measureUnits: measurementSystem,
          showPopup: true,
          callback: this.updatePointInfoPopupSubject.bind(this),
        });
        break;
      }
      default: {
        return null;
      }
    }
    return new MeasurementTool(toolTypeId, externalTool, false, mapId);
  }

  updateGroundElevationPopupSubject(event: any): void {
    const popup: IPopup = {
      coordinates: event.point,
      epsg: event.epsg,
      type: "ground-elevation",
      data: {
        altitude: event.altitude.value,
        units: event.unit,
        latitude: event.point[1],
        longitude: event.point[0],
      },
    };
    this.groundElevationPopupSubject.next(popup);
  }

  updatePointInfoPopupSubject(event: any): void {
    const popup: IPopup = {
      coordinates: event.point,
      epsg: event.epsg,
      type: "point-info",
      data: {},
    };
    this.pointInfoPopupSubject.next(popup);
  }
}
